import {
	ChangeDetectorRef,
	Component,
	QueryList,
	ViewChildren,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ErrorService } from "@services/error.service";
import { ExpedienteService } from "@services/expediente.service";
import { SessionService } from "@services/session.service";
import AccionesFuncionalidad from "@shared/enums/acciones.enum";
import { DangerError, WarningError } from "@shared/errors/custom.error";
import inicializarDataTable, {
	obtenerDatos,
	obtenerTabla,
} from "@shared/helpers/datatables.helper";
import { PrestadorService } from "@services/prestador.service";
import { LicenciaService } from "@services/licencia.service";
import { BaseFuncionalidadComponent } from "@shared/base/base-funcionalidad.component";
import { AlertService } from "@services/alert.service";
import ExpedienteEstados from "@shared/enums/expediente-estados.enum";
import { Location } from "@angular/common";
import { PdfService } from "@services/pdf.service";
import { StateService } from "@services/state.service";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Licencia } from "@models/licencia.model";
import { base64DataUrlToBlob } from "@shared/utils/base64toBlob.utils";
import { TerminologiaService } from "@services/terminologia.service";
import sprintf from "@shared/utils/sprintf.util";
import { Mensajes } from "@shared/enums/mensajes.enum";
import Funcionalidades from "@shared/enums/funcionalidades.enum";
import { Archivo } from "@models/archivo.model";
import LicenciaEstados from "@shared/enums/licencia-estados.enum";
import { SancionService } from "@services/sancion.services";
import { ApelacionEstados } from "@shared/enums/apelacion-estados.enum";
import IEstado, { IEstadoApelacionParcial } from "@models/estado.interface";
import { ApelacionParcialEstados } from "@shared/enums/apelacion-parcial-estados.enum";
import { UploadDocumentComponent } from "@shared/components/documentos/upload/upload.component";
import { GeneradorPdfService } from "@services/generador-pdf.service";
import { NotificacionService } from "@services/notificacion.service";
import IMotivo from "@models/motivo.interface";

@Component({
	selector: "app-expedientes-detalle",
	templateUrl: "./expedientes-detalle.component.html",
	styleUrls: ["./expedientes-detalle.component.scss"],
})
export class ExpedientesDetalleComponent extends BaseFuncionalidadComponent {
	constructor(
		sessionService: SessionService,
		route: ActivatedRoute,
		location: Location,
		router: Router,
		stateService: StateService,
		private errorService: ErrorService,
		private expedienteService: ExpedienteService,
		private licenciaService: LicenciaService,
		private prestadorService: PrestadorService,
		private toast: AlertService,
		private pdfService: PdfService,
		private sanitizer: DomSanitizer,
		private terminologiaService: TerminologiaService,
		private sancionService: SancionService,
		private cdr: ChangeDetectorRef,
		private generadorPdfService: GeneradorPdfService,
		private notificacionService: NotificacionService,
		private fb: FormBuilder,
	) {
		super(route, sessionService, location, router, stateService);

		// RFC1: catálogo cerrado de motivos de rechazo (viene desde terminología/BD)
		try {
			this.rejectionReasons = this.terminologiaService.obtenerMotivosRechazoExpediente();
		} catch {
			this.rejectionReasons = [];
		}

		this.rejectionForm = this.fb.group({
			reason: [null, [Validators.required]],
			justification: [null]
		});

		this.rejectionForm.get('reason')?.valueChanges.subscribe(reasonCodigo => {
			const justificationControl = this.rejectionForm.get('justification');
			const motivoSeleccionado = this.rejectionReasons.find(m => m.codigo === reasonCodigo);
			if (motivoSeleccionado?.descripcion === 'requiere_justificacion') {
				justificationControl?.setValidators([Validators.required, Validators.maxLength(250)]);
			} else {
				justificationControl?.clearValidators();
				justificationControl?.setValue(null);
			}
			justificationControl?.updateValueAndValidity();
		});

		this.returnForm = this.fb.group({
			observacion: [null, [Validators.required, Validators.maxLength(100)]]
		});
	}

	@ViewChildren(UploadDocumentComponent)
	uploadDocument!: QueryList<UploadDocumentComponent>;

	licencias: Licencia[] = []; // Variable que guarda la información de las licencias medicas que se muestran en la tabla

	mostrarModalAprobacion = false; // Variable que maneja el renderizado del modal de aprobación de expediente
	mostrarModalRechazo = false; // Variable que maneja el renderizado del modal de rechazo de expediente
	mostrarModalEnvioAntecedentes = false; // Variable que maneja el renderizado del modal de envío de antecedentes
	mostrarModalAprobacionProrroga = false; // Variable que maneja el renderizado del modal de aprobación de la prórroga
	mostrarModalRechazoProrroga = false; // Variable que maneja el renderizado del modal de rechazo de la prórroga
	mostrarModalFirmarResolucion = false; // Variable que maneja el renderizado del modal de firmar la resolución de la sanción
	mostrarModalDescargarResolucion = false; // Variable que maneja el renderizado del modal de descarga del PDF de resolución
	tdeCodigoResolucion: number = 3; // tde_codigo del documento generado por la firma de resolución
	mostrarModalCerrarExpediente = false;
	mostrarModalPreviewResolucion = false; // Variable que maneja el renderizado del modal preview del pdf de resolución
	mostrarModalAprobacionApelacion = false; // Variable que maneja el renderizado del modal de aprobación de la apelación
	mostrarModalRechazoApelacion = false; // Variable que maneja el renderizado del modal de rechazo de la apelación
	mostrarModalResumenApelacion = false; // Variable que maneja el renderizado del modal de resumen de respuesta de la apelación
	mostrarModalPreviewSUSESO = false; // Variable que maneja el renderizado del modal del preview del documento enviado por SUSESO en el resumen de respuesta de la apelación
	urlArchivoProrroga: string;
	motivoRechazoProrroga: string;
	motivoApelacion: string;
	descargarArchivoLoading: boolean = false;
	mostrarModalMarcarApelacion = false;

	motivoRechazo: string; // variable que guarda el motivo de rechazo del expediente
	mensajeExito: string; // variable que guarda el mensaje de exito en la acción del expediente
	tituloModalConfirmacion: string; // variable que guarda el titulo del modal de confirmación segun la acción que se este ejecutando
	tituloModalResumen: string = ""; // variable que guarda el titulo del modal de verificación

	pdfResolucionBase64: string; // Documento pdf de la resolucion en base64
	pdfResolucionUrl: SafeResourceUrl; // Url del documento pdf de la resolucion en base64

	sanciones: any; // Sanciones aplicadas al expediente

	mensajeSinResultados: string; // Mensaje de respuesta de la BD

	documentoSeleccionado: Archivo | null = null; // Variable que guarda el documento seleccionado
	estadoApelacionSeleccionado: IEstado | null = null; // Opción de acogimiento parcial seleccionada
	sancionAnuladaSeleccionada: IEstadoApelacionParcial | null = null; // Opción de sanción anulada para el acogimiento parcial seleccionado

	urlTemporalDocumentoSUSESO: SafeResourceUrl;

	rejectionForm: FormGroup;
	rejectionReasons: IMotivo[]; // RFC1: catálogo cerrado de motivos de rechazo (desde terminología)

	mostrarModalDevolucion = false;
	returnForm: FormGroup;

	formatearEstado(nombreEstado: string): string {
		if (!nombreEstado) return '';

		const estadoLimpio = nombreEstado.trim();
		const estadoObjetivo = "Con antecedentes ingresados completos, no todos cumplen";

		if (estadoLimpio === estadoObjetivo) {
			return "Con antecedentes ingresados completos";
		}

		return nombreEstado;
	}
	
	ngOnInit() {
		this.verExpediente();
		this.obtenerDiasHabilesRestantes();
	}

	get estadosApelacionAcogida(): IEstado[] {
		return this.terminologiaService.obtenerEstadoApelacionPorID([
			ApelacionEstados.acogimiento_parcial,
			ApelacionEstados.acogimiento_total,
		]);
	}

	get estadosApelacionAcogidaParcial(): IEstadoApelacionParcial[] {
		return this.terminologiaService.obtenerEstadosApelacionParcial();
	}

	get esAcogimientoParcial(): boolean {
		return (
			this.estadoApelacionSeleccionado?.codigo ===
			ApelacionEstados.acogimiento_parcial
		);
	}

	get esApelacionRechazada(): boolean {
		return (
			this.estadoApelacionSeleccionado?.codigo === ApelacionEstados.rechazada
		);
	}

	get mostrarBotonRechazarApelacion(): boolean {
		return (
			this.canRejectApelation &&
			this.expediente.estado.codigo === ExpedienteEstados.apelacion_suseso
		);
	}

	get mostrarBotonAcogerApelacion(): boolean {
		return (
			this.canApproveApelation &&
			this.expediente.estado.codigo === ExpedienteEstados.apelacion_suseso
		);
	}

	get esMotivoOtro(): boolean {
		const codigo = this.rejectionForm.get('reason')?.value;
		return this.rejectionReasons?.find(m => m.codigo === codigo)?.descripcion === 'requiere_justificacion';
	}

	get puedeDevolver(): boolean {
		// RFC2/RFC6: solo disponible para el Presidente en "Revisar resoluciones pendientes
		// de firma", único punto del flujo donde ya existe una evaluación del fiscalizador
		// (pauta cerrada) que se pueda devolver para corrección. En "Revisar expedientes
		// pendientes de aprobación" el fiscalizador aún no ha intervenido, por lo que
		// devolver no aplica ahí (solo Aprobar/Rechazar, cubierto por RFC1).
		const etapaValida = this.idFuncionalidad === Funcionalidades.revisar_resoluciones_a_firmar;
		// RFC5: el botón solo debe mostrarse o estar habilitado si devoluciones === 0
		return this.canReturn && etapaValida && this.expediente?.devoluciones === 0;
	}

	get mostrarMultaPosteriorApelacion(): boolean {
		return (
			this.expediente.sancion?.monto &&
			(this.esApelacionRechazada ||
				(this.esAcogimientoParcial &&
					this.sancionAnuladaSeleccionada.codigo ===
					ApelacionParcialEstados.cesar_suspencion))
		);
	}

	get mostrarSuspensionPosteriorApelacion(): boolean {
		return (
			this.expediente.sancion?.dias &&
			(this.esApelacionRechazada ||
				(this.esAcogimientoParcial &&
					this.sancionAnuladaSeleccionada.codigo ===
					ApelacionParcialEstados.multa_sin_efecto))
		);
	}

	get mensajeSinPermisos(): string {
		return this.terminologiaService.obtenerMensajePorID(
			Mensajes.sin_permisos_funcionalidad,
		);
	}

	get mensajeSinPermisosAccion(): string {
		return this.terminologiaService.obtenerMensajePorID(
			Mensajes.sin_permisos_accion,
		);
	}

	get mensajeAprobarExpediente(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.aprobar_expediente,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeRechazarExpediente(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.rechazar_expediente,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeEnviarAntecedentes(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.enviar_antecedentes,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeFirmarResolucion(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.firmar_resolucion,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeDevolverExpediente(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.devolver_expediente,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeCerrarExpediente(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.cerrar_expediente,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeAprobarProrroga(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.aprobar_prorroga,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeRechazarProrroga(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.rechazar_prorroga,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeAprobarApelacion(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.aprobar_apelacion,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeRechazarApelacion(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.rechazar_apelacion,
		);
		return sprintf(mensaje, this.expediente.id);
	}

	get mensajeConfirmacionRespuesta(): string {
		return this.esApelacionRechazada
			? this.mensajeRechazarApelacion
			: this.mensajeAprobarApelacion;
	}

	get todosAntecedentesCompletos(): boolean {
		return this.licencias.every(
			(licencia) =>
				licencia.estado.codigo === LicenciaEstados.antecedentes_completos,
		);
	}

	get mostrarBotonMarcarApelacion(): boolean {
		return (
			this.canApproveApelation &&
			this.expediente?.estado?.codigo === ExpedienteEstados.resolucion_firmada
		);
	}

	get mensajeMarcarApelacion(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.marcar_apelacion,
		);
		return sprintf(
			mensaje ?? "¿Está seguro de marcar el expediente N°%s con apelación?",
			this.expediente.id,
		);
	}

	async verExpediente() {
		this.isLoading = true;
		try {
			if (!this.canRead) throw new DangerError(this.mensajeSinPermisosAccion);
			if (!this.id) throw new WarningError("Expediente no seleccionado");
			const promesas = this.construirPromesasPorFuncionalidad();
			const [licencias, prestador] = await Promise.all(promesas);
			if (!this.prestador) this.prestador = prestador;
			this.licencias = licencias;
			const noOrdenar = [];
			if (this.canReadDoc) noOrdenar.push(-1);
			if (this.canSignSanction) await this.crearPDFResolucion();
			inicializarDataTable(
				"#tabla-licencias",
				noOrdenar,
				this.stateService.getEstadoDatatableLicencia(),
				50,
			);
			setTimeout(() => {
				const tabla = obtenerTabla();
				tabla.on("search.dt order.dt page.dt", (e) => {
					this.stateService.setEstadoDatatableLicencia(
						obtenerDatos("#tabla-licencias"),
					);
				});
			}, 0);
		} catch (error) {
			if (error instanceof DangerError)
				this.mensajeSinResultados = error.message;
			else this.errorService.handle(error, "Error al obtener las licencias.");
		} finally {
			this.isLoading = false;
		}
	}

	async obtenerDiasHabilesRestantes() {
		if (
			this.idFuncionalidad === Funcionalidades.revisar_expedientes_historial ||
			typeof this.expediente.dias_habiles === "number"
		)
			return;
		try {
			if (!this.id || !this.expediente)
				throw new WarningError("Expediente no seleccionado");
			if (!this.expediente.fecha_termino)
				throw new WarningError("Expediente de término no encontrada");
			const diasRestantes =
				await this.expedienteService.obtenerDiasHabilesRestantes(
					this.expediente.getFechaTerminoSinHoraConBarras(),
				);
			this.stateService.updateDiasHabilesExpediente(diasRestantes);
		} catch (error) {
			this.errorService.handle(error, "Error al obtener los días hábiles.");
		} finally {
			this.isLoading = false;
		}
	}

	private construirPromesasPorFuncionalidad(): Promise<any>[] {
		const rutSinDV = this.usuario.getRutSinDV();

		const promesas: Promise<any>[] = [
			this.licenciaService.obtenerLicenciasPorExpediente(this.id),
		];
		if (!this.prestador && this.isProveedorFuncionality)
			promesas.push(this.prestadorService.obtenerInformacion(rutSinDV));
		return promesas;
	}

	async crearPDFResolucion() {
		if (!this.canSignSanction) return;
		try {
			this.pdfResolucionBase64 = await this.pdfService.generarPdfResolucion(
				this.licencias,
				this.expediente.prestador,
			);
			const source = `data:application/pdf;base64,${this.pdfResolucionBase64}`;
			this.pdfResolucionUrl =
				this.sanitizer.bypassSecurityTrustResourceUrl(source);
		} catch (error) {
			this.errorService.handle(error, "Error al aprobar el expediente.");
		}
	}

	abrirModalPreview(resolucion: boolean = true) {
		if (resolucion) this.mostrarModalPreviewResolucion = true;
		else {
			// URL temporal para ver el documento en el resumen
			this.urlTemporalDocumentoSUSESO =
				this.sanitizer.bypassSecurityTrustResourceUrl(
					"data:application/pdf;base64," + this.documentoSeleccionado.base64,
				);
			this.mostrarModalPreviewSUSESO = true;
		}
	}

	abrirModal(accion: number) {
		this.motivoRechazo = "";
		this.mensajeExito = "";
		this.estadoApelacionSeleccionado = null;
		this.sancionAnuladaSeleccionada = null;
		this.motivoApelacion = "";
		this.documentoSeleccionado = null;

		try {
			switch (accion) {
				case AccionesFuncionalidad.aprobar_expediente:
					if (!this.canApprove)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalAprobacion = true;
					break;
				case AccionesFuncionalidad.rechazar_expediente:
					if (!this.canReject)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalRechazo = true;
					this.rejectionForm.reset();
					break;
				case AccionesFuncionalidad.enviar_antecedentes:
					if (!this.canSendDoc)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalEnvioAntecedentes = true;
					break;
				case AccionesFuncionalidad.aprobar_prorroga:
					if (!this.canApproveExtend)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalAprobacionProrroga = true;
					break;
				case AccionesFuncionalidad.rechazar_prorroga:
					if (!this.canRejectExtend)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalRechazoProrroga = true;
					break;
				case AccionesFuncionalidad.firmar_resolucion:
					if (!this.canSignSanction)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalFirmarResolucion = true;
					break;
				case AccionesFuncionalidad.cerrar_expediente:
					if (!this.canCloseExpedient)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalCerrarExpediente = true;
					break;
				case AccionesFuncionalidad.aprobar_apelacion:
					if (!this.canApproveApelation)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalAprobacionApelacion = true;
					break;
				case AccionesFuncionalidad.rechazar_apelacion:
					if (!this.canRejectApelation)
						throw new DangerError(this.mensajeSinPermisosAccion);
					this.mostrarModalRechazoApelacion = true;
					this.estadoApelacionSeleccionado =
						this.terminologiaService.obtenerEstadoApelacionPorID([
							ApelacionEstados.rechazada,
						])[0];
					break;
				case AccionesFuncionalidad.apelacion_suseso:
					this.mostrarModalMarcarApelacion = true;
					break;
			}
		} catch (error) {
			this.errorService.handle(error, "Error al abrir el modal.");
		}
	}

	/** Abre el modal de confirmación y limpia estados residuales de la acción previa. */
	private abrirModalConfirmacion(): void {
		this.estadoApelacionSeleccionado = null;
		this.sancionAnuladaSeleccionada = null;
		this.motivoApelacion = "";
		this.documentoSeleccionado = null;
		this.mostrarModalConfirmacion = true;
	}

	/**
	 * Maneja el cierre por cancelación (X, Cerrar, Escape o clic fuera).
	 * En el flujo de apelación, al cancelar desde el resumen se reabre el
	 * modal anterior para permitir corregir la información.
	 */
	onModalCerrado() {
		if (
			this.idFuncionalidad === Funcionalidades.revisar_apelaciones &&
			this.tituloModalResumen.trim()
		) {
			if (this.esApelacionRechazada) this.mostrarModalRechazoApelacion = true;
			else this.mostrarModalAprobacionApelacion = true;
			this.cdr.detectChanges();
			this.tituloModalResumen = "";
		}
	}

	onModalConfirmacionCerrado() {
		this.mostrarModalConfirmacion = false;
		this.volverAtras(); // Vuelve atras al cerrar el modal de confirmación
	}

	/** Se dispara al cancelar los modales de aprobar/rechazar apelación para limpiar el documento adjunto. */
	onModalRespuestaApelacionCerrado() {
		this.uploadDocument.forEach((upload) => upload.limpiarDocumento());
	}

	// En ExpedientesDetalleComponent:
	async aprobarExpediente() {
		this.isLoadingModal = true;
		try {
			if (!this.canApprove)
				throw new DangerError(this.mensajeSinPermisosAccion);

			// Toda la lógica compleja ahora está oculta dentro de esta simple llamada:
			this.mensajeExito = await this.expedienteService.aprobarExpediente(
				this.expediente.id,
			);

			this.tituloModalConfirmacion = "Aprobar expediente";
			this.mostrarModalAprobacion = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(error, "Error al aprobar el expediente.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	async rechazarExpediente() {
		this.isLoadingModal = true;
		try {
			if (!this.canReject) throw new DangerError(this.mensajeSinPermisosAccion);
			if (this.rejectionForm.invalid) {
				throw new WarningError("Debe completar los campos obligatorios para el rechazo.");
			}

			const mrxCodigo: number = this.rejectionForm.value.reason;
			const motivoSeleccionado = this.rejectionReasons.find(m => m.codigo === mrxCodigo);
			const justificacion = motivoSeleccionado?.descripcion === 'requiere_justificacion'
				? this.rejectionForm.value.justification.trim()
				: null;

			this.mensajeExito = await this.expedienteService.rechazarExpediente(
				this.expediente.id,
				mrxCodigo,
				justificacion
			);
			this.tituloModalConfirmacion = "Rechazar el expediente";
			this.mostrarModalRechazo = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(error, "Error al rechazar el expediente.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	async confirmarEnvioAntecedentes() {
		this.isLoadingModal = true;
		try {
			if (!this.canSendDoc)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.mensajeExito = await this.expedienteService.enviarAntecedentes(
				this.id,
				this.expediente.estado.codigo,
			);
			this.mostrarModalEnvioAntecedentes = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al enviar los antecedentes.",
			);
		} finally {
			this.isLoadingModal = false;
		}
	}

	abrirModalDevolucion() {
		try {
			if (!this.canReturn) throw new DangerError(this.mensajeSinPermisosAccion);
			this.mostrarModalDevolucion = true;
			this.returnForm.reset();
		} catch (error) {
			this.errorService.handle(error, "Error al abrir el modal.");
		}
	}

	async devolverExpediente() {
		this.isLoadingModal = true;
		try {
			if (!this.canReturn) throw new DangerError(this.mensajeSinPermisosAccion);
			if (this.returnForm.invalid) {
				throw new WarningError("Debe ingresar una observación válida (máx. 100 caracteres).");
			}

			const observacion = this.returnForm.value.observacion.trim();
			this.mensajeExito = await this.expedienteService.devolverExpediente(
				this.expediente.id,
				this.expediente.estado.codigo,
				observacion
			);

			this.tituloModalConfirmacion = "Devolver expediente al fiscalizador";
			this.mostrarModalDevolucion = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(error, "Error al devolver el expediente al fiscalizador.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	goProrroga() {
		const prorrogaSolicitada = [
			ExpedienteEstados.solicitud_prorroga,
			ExpedienteEstados.prorroga_aprobada,
			ExpedienteEstados.prorroga_rechazada,
		].includes(this.expediente.estado.codigo);
		try {
			if (
				(!this.canRequestExtend &&
					this.expediente.estado.codigo === ExpedienteEstados.aprobado) ||
				(!this.canReadExtend && prorrogaSolicitada)
			)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.router.navigate(["prorroga"], {
				relativeTo: this.route,
			});
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al intentar cambiar de página.",
			);
		}
	}

	goVerDocumentos(licencia: Licencia) {
		this.stateService.setLicencia(licencia);
		try {
			if (!this.canReadDoc)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.router.navigate(["licencia"], {
				relativeTo: this.route,
			});
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al intentar cambiar de página.",
			);
		}
	}

	goVerDocumentosExpediente() {
		try {
			if (!this.canReadDocumentsE)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.router.navigate(["ver-documentos"], {
				relativeTo: this.route,
			});
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al intentar cambiar de página.",
			);
		}
	}

	goHistorial() {
		try {
			if (!this.canReadHistory)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.router.navigate(["estados"], {
				relativeTo: this.route,
			});
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al intentar cambiar de página.",
			);
		}
	}

	goVerApelacion() {
		try {
			if (!this.canReadApelation && !this.canMakeApelation)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.router.navigate(["apelacion"], {
				relativeTo: this.route,
			});
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al intentar cambiar de página.",
			);
		}
	}

	async firmarResolucion() {
		this.isLoadingModal = true;
		try {
			if (!this.canSignSanction)
				throw new DangerError(this.mensajeSinPermisosAccion);

			const p_prestador = this.extraerRutSinDv(this.expediente?.prestador?.rut);
			if (!p_prestador)
				throw new WarningError("No se encontró el rut del prestador (sin DV).");

			const p_pasada = this.expediente.iteracion;

			// Ejecutar cálculo de sanción en backend
			const resSancion = await this.sancionService.ejecutarSancion(
				this.expediente.id,
				this.expediente.cumplimiento,
				p_prestador,
				p_pasada,
			);

			// Determinar template según resultado
			const templateId = resSancion.tiene_sancion
				? this.templateSancion(p_pasada)
				: 3;

			// Generar PDF de resolución y cambiar estado
			const { msg, tde_codigo } = await this.expedienteService.firmarResolucion(
				this.expediente.id,
				this.expediente.estado.codigo,
				templateId,
			);

			this.mensajeExito = msg;
			this.tdeCodigoResolucion = tde_codigo;
			this.mostrarModalFirmarResolucion = false;
			this.mostrarModalDescargarResolucion = true;
		} catch (error) {
			this.errorService.handle(error, "Error al firmar la resolución.");
		} finally {
			this.isLoadingModal = false;
		}
	}
	private templateSancion(pasada: number): number {
		 // Primera sanción (multa o multa + días)
		if (pasada === 1) return 4;          
		// Sanción reactivada    
		if (pasada >= 2 && pasada <= 3) return 5; 
		// Pasada 4+
		return 6; 
	}                                
	
	async confirmarDescargaResolucion() {
		this.isLoadingModal = true;
		try {
			await this.expedienteService.descargarResolucionFirmada(this.expediente.id, this.tdeCodigoResolucion);
		} catch (error) {
			this.errorService.handle(error, "Error al descargar la resolución firmada.");
		} finally {
			this.isLoadingModal = false;
			this.mostrarModalDescargarResolucion = false;
			setTimeout(() => this.abrirModalConfirmacion());
		}
	}


	rechazarDescargaResolucion() {
		this.mostrarModalDescargarResolucion = false;
		setTimeout(() => this.abrirModalConfirmacion());
	}

	private extraerRutSinDv(rut: any): number {
		if (rut === null || rut === undefined) return 0;

		const s = String(rut).trim().replace(/\./g, "");
		const base = s.includes("-") ? s.split("-")[0] : s;

		const n = Number(base);
		return Number.isFinite(n) ? n : 0;
	}


	async cerrarExpediente() {
		this.isLoadingModal = true;
		try {
			if (!this.canCloseExpedient)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.mensajeExito = await this.expedienteService.cerrarExpediente(
				this.expediente,
			);
			this.mostrarModalCerrarExpediente = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(error, "Error al aprobar el expediente.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	async aprobarProrroga() {
		this.isLoadingModal = true;
		try {
			if (!this.canApproveExtend)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.mensajeExito =
				await this.expedienteService.aprobarRechazarExpediente(
					this.expediente.id,
					this.expediente.estado.codigo,
					ExpedienteEstados.prorroga_aprobada,
					null,
				);
			this.tituloModalConfirmacion = "Aprobar solicitud de ampliación de plazo";
			this.mostrarModalAprobacionProrroga = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(error, "Error al aprobar la prórroga.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	async rechazarProrroga() {
		this.isLoadingModal = true;
		try {
			if (
				!this.motivoRechazoProrroga ||
				this.motivoRechazoProrroga.trim().length === 0
			) {
				this.toast.warning("Debe ingresar el motivo del rechazo.");
				return;
			}
			if (!this.canApproveExtend)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.mensajeExito =
				await this.expedienteService.aprobarRechazarExpediente(
					this.expediente.id,
					this.expediente.estado.codigo,
					ExpedienteEstados.prorroga_rechazada,
					this.motivoRechazoProrroga.trim(),
				);
			this.tituloModalConfirmacion =
				"Rechazar solicitud de ampliación de plazo";
			this.mostrarModalRechazoProrroga = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(error, "Error al aprobar la prórroga.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	async descargarArchivoProrroga() {
		this.descargarArchivoLoading = true;
		try {
			// Obtén el archivo desde el backend
			this.expediente.motivo.archivo =
				await this.expedienteService.obtenerDocumentoProrroga(this.id);
			const archivo = this.expediente.motivo.archivo;
			let base64 = archivo?.url_documento;
			if (!base64) {
				this.toast.warning("No hay archivo de prórroga disponible.");
				return;
			}

			const blobUrl = base64DataUrlToBlob(base64);

			window.open(blobUrl, "_blank");
		} catch (error) {
			this.errorService.handle(
				error,
				"Error al obtener la información del documento.",
			);
		} finally {
			this.descargarArchivoLoading = false;
		}
	}

	async guardarRespuesta() {
		this.isLoadingModal = true;
		try {
			this.mensajeExito =
				await this.expedienteService.respuestaApelacionExpediente(
					this.expediente.id,
					this.documentoSeleccionado,
					this.motivoApelacion,
					this.estadoApelacionSeleccionado.codigo,
					this.sancionAnuladaSeleccionada?.codigo,
				);
			this.tituloModalConfirmacion = this.nameAction(
				this.esApelacionRechazada
					? AccionesFuncionalidad.rechazar_apelacion
					: AccionesFuncionalidad.aprobar_apelacion,
			);
			this.mostrarModalResumenApelacion = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(
				error,
				"Error al guardar la respuesta de la apelación.",
			);
		} finally {
			this.isLoadingModal = false;
		}
	}

	async aprobarApelacion() {
		this.isLoadingModal = true;
		try {
			if (!this.canApproveApelation)
				throw new DangerError(this.mensajeSinPermisosAccion);

			// Validación el estado de la apelación
			if (
				![
					ApelacionEstados.acogimiento_parcial,
					ApelacionEstados.acogimiento_total,
				].includes(this.estadoApelacionSeleccionado.codigo)
			) {
				throw new WarningError(
					"Debe seleccionar Acogimiento Total o Acogimiento parcial.",
				);
			}

			// Validación accion si el estado es acogida parcial
			if (
				this.estadoApelacionSeleccionado?.codigo ===
				ApelacionEstados.acogimiento_parcial &&
				!this.sancionAnuladaSeleccionada
			) {
				throw new WarningError(
					"Debe seleccionar Cesar suspensión o Dejar sin efecto la multa.",
				);
			}

			// Validación documento obligatorio
			if (!this.documentoSeleccionado) {
				throw new WarningError("Debe adjuntar un documento.");
			}

			// Validación texto obligatorio y mínimo 5 caracteres
			this.motivoApelacion = this.motivoApelacion?.trim() || "";
			const motivo = this.motivoApelacion;
			if (motivo.length < 5) {
				throw new WarningError("El motivo debe tener al menos 5 caracteres.");
			}

			// Validación sin caracteres especiales
			if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9\s.,;:()\-_]+$/.test(motivo)) {
				throw new WarningError(
					"El motivo no puede contener caracteres especiales.",
				);
			}

			this.tituloModalResumen = "Resumen apelación acogida";
			this.mostrarModalAprobacionApelacion = false;
			this.mostrarModalResumenApelacion = true; // Encadena el modal de resumen tras aceptar
		} catch (error) {
			this.errorService.handle(error, "Error al aprobar la apelación.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	async rechazarApelacion() {
		this.isLoadingModal = true;
		try {
			if (!this.canRejectApelation)
				throw new DangerError(this.mensajeSinPermisosAccion);

			// Validación documento obligatorio
			if (!this.documentoSeleccionado) {
				throw new WarningError("Debe adjuntar un documento.");
			}

			// Validación texto obligatorio y mínimo 5 caracteres
			const motivo = this.motivoApelacion?.trim() || "";
			if (motivo.length < 5) {
				throw new WarningError("El motivo debe tener al menos 5 caracteres.");
			}

			// Validación sin caracteres especiales
			if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9\s.,;:()\-_]+$/.test(motivo)) {
				throw new WarningError(
					"El motivo no puede contener caracteres especiales.",
				);
			}

			this.tituloModalResumen = "Resumen apelación rechazada";
			this.mostrarModalRechazoApelacion = false;
			this.mostrarModalResumenApelacion = true; // Encadena el modal de resumen tras aceptar
		} catch (error) {
			this.errorService.handle(error, "Error al rechazar la apelación.");
		} finally {
			this.isLoadingModal = false;
		}
	}

	asignarDocumento(file: Archivo) {
		this.documentoSeleccionado = file;
	}

	// Cambia la opción en el radio de estado apelación en el modal de acoger apelación
	onEstadoApelacionChange(estadoApelacion: IEstado) {
		this.estadoApelacionSeleccionado = estadoApelacion;
		this.sancionAnuladaSeleccionada = null;
	}

	// Cambia la opción en el radio de estado apelación parcial en el modal de acoger apelación
	onSancionAnuladaChange(estadoApelacionParcial: IEstadoApelacionParcial) {
		this.sancionAnuladaSeleccionada = this.esAcogimientoParcial
			? estadoApelacionParcial
			: null;
	}

	async marcarComoApelado() {
		this.isLoadingModal = true;
		try {
			this.mensajeExito = await this.expedienteService.marcarComoApelado(
				this.expediente.id,
			);
			this.tituloModalConfirmacion = "Marcar expediente con apelación";
			this.mostrarModalMarcarApelacion = false;
			this.abrirModalConfirmacion();
		} catch (error) {
			this.errorService.handle(
				error,
				"Error al marcar el expediente como apelado.",
			);
		} finally {
			this.isLoadingModal = false;
		}
	}
}
